/**
 * useAutoFocusFirstControl / focusFirstControl — the shared "focus the first usable
 * control" helper for mouse-free operation (CLAUDE.md standing rule 14, ADR-0013).
 *
 * The G6 keyboard-navigation rule: whenever a FORM opens (or a detail / popup is
 * shown), focus lands on the first usable control so the user can start straight
 * away with the keyboard — the non-grid counterpart to <AppDataGrid>'s
 * auto-focus-first-row. Priority order matches the control-type standard:
 *   text / date / number field · combo / dropdown · select · textarea · list ·
 *   contenteditable · then a tab · then (optionally) a button.
 * A text field also gets the cursor placed at its end so typing works immediately.
 * A native <select> and the shared combos already keep a selected item, and become
 * arrow-navigable the moment they hold focus (↑/↓ change, Enter confirm, Alt+↓
 * opens, typing finds) — so "focus + first item selected" is satisfied by focusing
 * them; we deliberately do NOT auto-open a dropdown.
 */
import { useEffect, type RefObject } from 'react';

// A form's first meaningful entry point — real fields + combos/lists. Skips
// hidden / disabled / readonly, and lone checkbox/radio (rarely the entry point).
const FIELD_SELECTOR = [
  'input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([disabled]):not([readonly])',
  'textarea:not([disabled]):not([readonly])',
  'select:not([disabled])',
  '[contenteditable="true"]',
  '[role="combobox"]:not([aria-disabled="true"])',
  '[role="listbox"]:not([aria-disabled="true"])',
].join(',');

const TAB_SELECTOR = '[role="tab"]:not([aria-disabled="true"])';
// A keyboard-navigable grid ROW — the first row of the shared <AppDataGrid> (roving
// tabindex) or a <ModuleListView> CRUD list. So a popup whose main content is a GRID
// (a picker / link popup with no leading form field) focuses its first row instead of
// falling through to a Close / toolbar button — the "appropriate control OR grid"
// standard (user report 2026-09-21). Ranked after fields/tabs, before buttons.
const GRID_ROW_SELECTOR = '.adg tbody tr[tabindex], .mlv-nav';
// Radio / checkbox — the ENTRY point only when there is nothing higher-priority
// (a radio-group popup like "Registration Options": Regular / Pre-scheduled). Ranked
// after fields/tabs/grid so a normal form still focuses its first real field, but
// BEFORE a button so a radio-only popup takes focus (else focus stays on whatever was
// behind the modal and Enter leaks to it — user report 2026-09-21). FIELD_SELECTOR
// deliberately excludes lone radio/checkbox; this is the deliberate fallback.
const RADIO_CHECK_SELECTOR = 'input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled])';
const BUTTON_SELECTOR = 'button:not([disabled])';

// Visible enough to focus. Uses attribute / inline-style signals (which work in a
// real browser AND in jsdom, unlike offsetParent which needs a layout engine).
// Collapsed sections in this app conditionally render (element absent) or use the
// `hidden` attribute / inline display:none, so this catches the real cases.
function isVisible(el: Element): boolean {
  for (let n: HTMLElement | null = el as HTMLElement; n; n = n.parentElement) {
    if (n.hidden) return false;
    const st = n.style;
    if (st && (st.display === 'none' || st.visibility === 'hidden')) return false;
  }
  return true;
}

/**
 * Focus the first usable control inside `root`. Fields first, then a tab, then
 * (unless `includeButtons` is false) a button. Returns the focused element or null.
 */
/** Place focus and (for text-like inputs) the caret at end. */
function doFocus(el: HTMLElement): HTMLElement {
  el.focus({ preventScroll: true });
  if (el instanceof HTMLInputElement && /^(text|search|url|tel|email|password)$/.test(el.type)) {
    try { const n = el.value.length; el.setSelectionRange(n, n); } catch { /* some input types disallow selection */ }
  }
  return el;
}

export function focusFirstControl(
  root: HTMLElement | null,
  opts?: { includeButtons?: boolean },
): HTMLElement | null {
  if (!root) return null;

  // Honour [data-autofocus] nomination (shared convention — see focusFirstFormControl).
  const nominated = root.querySelector<HTMLElement>('[data-autofocus]');
  if (nominated && isVisible(nominated) && !nominated.matches('[disabled]')) return doFocus(nominated);

  const chain = [FIELD_SELECTOR, TAB_SELECTOR, GRID_ROW_SELECTOR, RADIO_CHECK_SELECTOR];
  if (opts?.includeButtons !== false) chain.push(BUTTON_SELECTOR);
  for (const sel of chain) {
    const el = Array.from(root.querySelectorAll<HTMLElement>(sel)).find(isVisible);
    if (el) return doFocus(el);
  }
  return null;
}

// Regions that are NOT the form body — a grid, any toolbar, or the list search box.
// Used so a FORM that also contains a grid (e.g. Sample Registration, which has the
// samples grid + a Project Notes grid) focuses its FIRST FORM FIELD at the top,
// not a grid row; while a pure LIST page (only a grid + a search/filter toolbar)
// has no form field and lets the grid self-focus its first row.
const FORM_EXCLUDE = '.adg,[role="toolbar"],.ca-toolbar,.mlv-toolbar,.mlv-toolbar-row,.adg-toolbar,.bl-toolbar';

const isSearchField = (el: Element): boolean =>
  el instanceof HTMLInputElement && (el.type === 'search' || /search/i.test(el.placeholder || ''));

/**
 * Focus the first FORM field in `root` — a field that is NOT inside a grid, a
 * toolbar, or the search box. Returns the focused element, or null when the page
 * has no such field (a pure list/grid page — leave focus for the grid's own
 * first-row auto-focus). The counterpart used by AppShell for form PAGES.
 */
export function focusFirstFormControl(root: HTMLElement | null): HTMLElement | null {
  if (!root) return null;

  // A form may nominate its preferred first-focus target via [data-autofocus].
  // This lets forms like Sample Registration (→ Client combo) or Result Entry
  // Express (→ QC Batch ID combo) land focus on the right control without
  // per-form bespoke code — the attribute is the common mechanism.
  const nominated = root.querySelector<HTMLElement>('[data-autofocus]');
  if (nominated && isVisible(nominated) && !nominated.matches('[disabled]')) return doFocus(nominated);

  const el = Array.from(root.querySelectorAll<HTMLElement>(FIELD_SELECTOR))
    .find((c) => isVisible(c) && !c.closest(FORM_EXCLUDE) && !isSearchField(c));
  if (!el) return null;
  return doFocus(el);
}

/**
 * Auto-focus the first usable control in `ref` when `active` turns true (form open
 * / detail entered / dialog shown). Guarded so it never steals focus from a field
 * the user is already in, nor from a control already inside this container.
 */
export function useAutoFocusFirstControl(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  opts?: { includeButtons?: boolean },
): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const ae = document.activeElement as HTMLElement | null;
    const inField = !!ae && (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) || ae.isContentEditable);
    if (inField) return;                       // don't steal from a field the user is typing in
    if (ae && root.contains(ae)) return;       // already focused inside this form
    focusFirstControl(root, opts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ref]);
}
